import { ReactElement, useEffect, useMemo, useRef } from 'react'

import { LoadingOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { TypeaheadOption, TypeaheadPagination } from 'types'

import * as styles from './styles.scss'
import TypeaheadMenuItem from './TypeaheadMenuItem'
import { TypeaheadMenuProps, TypeaheadMenuItemProps } from './types'

// How close to the bottom of the list the next page starts loading.
const LOAD_MORE_MARGIN = '0px 0px 40px 0px'

/**
 * The last row while more pages exist. It loads the next one whenever it comes into
 * view, so a list too short to scroll still pages, and the spinner only shows while a
 * page is on its way. If that page fails it offers a retry instead.
 */
function LoadMoreRow({
    pagination,
}: {
    pagination: TypeaheadPagination
}): ReactElement {
    const { t } = useTranslation('common')
    const ref = useRef<HTMLLIElement>(null)
    const loadMoreRef = useRef(pagination.loadMore)
    loadMoreRef.current = pagination.loadMore
    const { failed } = pagination

    useEffect(() => {
        const row = ref.current
        if (failed || !row || typeof IntersectionObserver === 'undefined') {
            return undefined
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    loadMoreRef.current()
                }
            },
            { root: row.parentElement, rootMargin: LOAD_MORE_MARGIN }
        )
        observer.observe(row)
        return () => observer.disconnect()
    }, [failed])

    return (
        // Not an option: keep it out of the listbox's items for assistive tech.
        <li ref={ref} role="presentation" className={styles['load-more']}>
            {failed ? (
                <>
                    {t('common:typeahead.loadMoreFailed', "Couldn't load more")}
                    <Button
                        type="link"
                        size="small"
                        onClick={() => pagination.loadMore()}
                    >
                        {t('common:typeahead.retry', 'Retry')}
                    </Button>
                </>
            ) : (
                <>
                    <LoadingOutlined />
                    {t('common:typeahead.loadingMore', 'Loading more')}
                </>
            )}
        </li>
    )
}

const defaultRenderItem = <ItemType extends TypeaheadOption = TypeaheadOption>(
    props: TypeaheadMenuItemProps<ItemType>
): ReactElement => <TypeaheadMenuItem {...props} />

const TypeaheadMenu = <ItemType extends TypeaheadOption = TypeaheadOption>({
    downshift,
    filterOptions = true,
    baseIndex = 0,
    title,
    selectLimit = Infinity,
    options = [],
    values,
    renderItem = defaultRenderItem,
    onToggleOption,
    pagination,
}: TypeaheadMenuProps<ItemType>): ReactElement | null => {
    const { inputValue, highlightedIndex, getMenuProps, getItemProps } =
        downshift

    const stringValues = useMemo(
        () => values.filter((v) => !!v).map((v) => v.value.toString()),
        [values]
    )
    const isSelectableItem = (item: TypeaheadOption): boolean =>
        !!item && !!item.label && !!item.value

    const filteredOptions = useMemo(
        () =>
            filterOptions && inputValue
                ? options.filter(
                      (item) =>
                          isSelectableItem(item) &&
                          !!item.label
                              ?.toLowerCase()
                              .includes(inputValue.toLowerCase())
                  )
                : options.filter((item) => isSelectableItem(item)),
        [filterOptions, inputValue, options]
    )

    return filteredOptions.length > 0 ? (
        <div className={styles.menu}>
            {title && <div className={styles['menu-title']}>{title}</div>}

            <ul {...getMenuProps({})}>
                {filteredOptions.map((item, index) => {
                    const stringValue = item.value.toString()
                    const highlightIndex = baseIndex + index
                    const highlighted = highlightedIndex === highlightIndex
                    const selected = stringValues.includes(stringValue)
                    const disabled = values.length === selectLimit && !selected

                    return (
                        <li
                            key={stringValue}
                            {...getItemProps({
                                index: highlightIndex,
                                item,
                                disabled,
                                className: classNames(
                                    styles['typeahead-item'],
                                    {
                                        [styles[
                                            'typeahead-item-option-active'
                                        ]]: highlighted,
                                        [styles[
                                            'typeahead-item-option-selected'
                                        ]]: selected,
                                        [styles[
                                            'typeahead-item-option-disabled'
                                        ]]: disabled,
                                    }
                                ),
                                onClick: () => onToggleOption(item),
                            })}
                        >
                            {renderItem({
                                item,
                                query: inputValue,
                                highlightQuery: filterOptions,
                                isSelected: selected,
                            })}
                        </li>
                    )
                })}
                {pagination?.hasMore && (
                    // Keyed on the row count so it re-observes after each page: if it
                    // is still in view, the next page loads too. Prefixed so it can't
                    // collide with an option's value.
                    <LoadMoreRow
                        key={`load-more-${filteredOptions.length}`}
                        pagination={pagination}
                    />
                )}
            </ul>
        </div>
    ) : null
}

export default TypeaheadMenu
